import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import { sentenceCase } from '../../../utils/utils';
import ActionButton from '../../elements/action-button/ActionButton';

const phoneRegex =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const ProjectManagerForm = ({ onSubmit, onClose, data }) => {
  const { t } = useTranslation();

  const schema = object({
    name: string()
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('name', { ns: 'fields' }),
          })
        )
      )
      .max(256, t('maxLength', { ns: 'validations', max: '256' })),
    email: string()
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('email', { ns: 'fields' }),
          })
        )
      )
      .email(
        sentenceCase(
          t('invalidFormat', {
            ns: 'validations',
            field: t('email', { ns: 'fields' }),
          })
        )
      )
      .max(256, t('maxLength', { ns: 'validations', max: '256' })),
    title: string().max(256, t('maxLength', { ns: 'validations', max: '256' })),
    company: string()
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('companyName', { ns: 'fields' }),
          })
        )
      )
      .max(256, t('maxLength', { ns: 'validations', max: '256 ' })),
    address: string()
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('address', { ns: 'fields' }),
          })
        )
      )
      .max(512, t('maxLength', { ns: 'validations', max: '512' })),
    phone: string()
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('phoneNumber', { ns: 'fields' }),
          })
        )
      )
      .matches(phoneRegex, {
        message: sentenceCase(
          `${t('invalidFormat', {
            ns: 'validations',
            field: t('phoneNumber', { ns: 'fields' }),
          })} ${t('example', { ns: 'validations', example: '030-1234-5678' })}`
        ),
      }),
  });

  const formInputs = [
    {
      name: 'name',
      label: t('name', { ns: 'fields' }),
    },
    {
      name: 'email',
      label: t('email', { ns: 'fields' }),
    },
    {
      name: 'title',
      label: t('jobTitle', { ns: 'fields' }),
    },
    {
      name: 'company',
      label: t('companyName', { ns: 'fields' }),
    },
    {
      name: 'phone',
      label: t('phoneNumber', { ns: 'fields' }),
    },
    {
      name: 'address',
      label: t('address', { ns: 'fields' }),
    },
  ];

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
  } = useForm({ mode: 'onChange', resolver: yupResolver(schema) });

  return (
    <>
      <form autoComplete="off">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {formInputs.map((formInput) => (
            <Controller
              key={formInput.name}
              name={formInput.name}
              control={control}
              defaultValue={data?.[formInput.name] || ''}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={formInput.label}
                  error={!!errors[formInput.name]}
                  helperText={errors?.[formInput.name]?.message}
                  inputProps={{ 'aria-label': formInput.name }}
                  fullWidth
                  size="small"
                />
              )}
            />
          ))}
        </Box>
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}
        >
          <Button variant="text" onClick={onClose} aria-label="cancel">
            {t('cancel', { ns: 'buttons' })}
          </Button>
          <ActionButton
            disabled={!isDirty || !isValid}
            aria-label="submit"
            clickAction={handleSubmit(onSubmit)}
          >
            {t('confirm', { ns: 'buttons' })}
          </ActionButton>
        </Box>
      </form>
    </>
  );
};

ProjectManagerForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
};

export default ProjectManagerForm;
